import type { Ticket } from '@trmediaab/zebra-types/trhub';
import { useCallback, useState } from 'react';
import useSWR from 'swr';

import type { ErrorWithMessage } from '../types/ErrorWithMessage';
import { ensureErrorWithMessage } from '../utils/ensureErrorWithMessage';
import request from '../utils/request';
import type { CheckoutState } from '.';
import { useImmutableState } from '.';
import useUser from './useUser';

const getTicketableSku = (order: NonNullable<CheckoutState['order']>) => {
  if (order.lineItems?.length !== 1) {
    return null;
  }

  const [lineItem] = order.lineItems;
  if (lineItem.product?.type === 'bettip') {
    return lineItem.product.sku;
  }

  return null;
};

export default function useTicket(order: NonNullable<CheckoutState['order']>) {
  const { user } = useUser();
  const { isCampaign } = useImmutableState();
  const sku = getTicketableSku(order);
  const ticketPaymentActive = !isCampaign && user != null && sku != null;
  const { data, error: fetchError } = useSWR<
    { tickets: Ticket[] },
    ErrorWithMessage
  >(ticketPaymentActive ? `ticket/${sku}` : null);
  const ticket =
    data != null && data.tickets.length > 0 ? data.tickets[0] : undefined;
  const [submitting, setSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState<ErrorWithMessage>();

  const punchTicket = useCallback(async () => {
    setSubmitting(true);
    try {
      if (ticket == null) {
        throw new Error('No ticket to punch');
      }
      await request('ticket/punch', {
        method: 'POST',
        body: {
          sku,
          ticket: ticket.id,
        },
      });
    } catch (error) {
      setTicketError(ensureErrorWithMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [sku, ticket]);

  const error = ticketError || fetchError;
  const isLoading =
    submitting || (ticketPaymentActive && data == null && error == null);

  return [ticket, error, isLoading, punchTicket] as const;
}
